import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { apiGetJson } from '../lib/apiClient'
import { unwrapList } from '../lib/apiUnwrap'
import { AnimatePresence, motion } from 'framer-motion'
import { backdropAnimation, fadeIn, modalAnimation, slideInBottom, staggerContainer } from '../lib/animations'

type PublicCustomerProof = {
  id: string
  title: string
  image_url: string
  created_at: string
  user_name: string
}

export default function CustomersPage() {
  const [loading, setLoading] = useState(true)
  const [proofs, setProofs] = useState<PublicCustomerProof[]>([])
  const [selectedProof, setSelectedProof] = useState<PublicCustomerProof | null>(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const resp = await apiGetJson<any>('/api/customer-proofs')
        setProofs(unwrapList<PublicCustomerProof>(resp))
      } catch {
        setProofs([])
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [])

  useEffect(() => {
    if (!selectedProof) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedProof(null)
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [selectedProof])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div className="text-center mb-8" {...fadeIn()}>
        <div className="text-sm font-semibold text-emerald-400 mb-2">UY TÍN & CHẤT LƯỢNG</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">Khách Hàng</h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-2">
          Hình ảnh giao dịch được tổng hợp từ khách hàng.
        </p>
      </motion.div>

      {loading ? (
        <div className="text-center text-sm text-gray-500 dark:text-gray-300">Đang tải...</div>
      ) : (
        <motion.div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" variants={staggerContainer(0.08)} initial="initial" whileInView="animate" viewport={{ once: true, margin: '-50px' }}>
          {proofs.map((p, index) => (
            <motion.div
              key={p.id}
              {...slideInBottom(index * 0.04)}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.35, delay: index * 0.04 }}
              whileHover={{ y: -4, scale: 1.01 }}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm"
            >
              <div
                className="aspect-[4/3] bg-gray-100 dark:bg-gray-800 cursor-zoom-in"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedProof(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setSelectedProof(p)
                }}
              >
                <img
                  src={p.image_url}
                  alt={p.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{p.title}</div>
                <div className="text-xs text-gray-500 dark:text-gray-300 truncate">{p.user_name}</div>
              </div>
            </motion.div>
          ))}
          {proofs.length === 0 && (
            <div className="col-span-full text-center text-sm text-gray-500 dark:text-gray-300 py-10">Chưa có dữ liệu</div>
          )}
        </motion.div>
      )}

      <AnimatePresence>
        {selectedProof && (
          <motion.div
            {...backdropAnimation}
            className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 pb-[env(safe-area-inset-bottom,0.5rem)] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Xem ảnh"
            onClick={() => setSelectedProof(null)}
          >
            <motion.div {...modalAnimation} className="relative w-full max-w-5xl my-auto" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="absolute -top-3 -right-3 sm:top-2 sm:right-2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 text-gray-900 shadow hover:bg-white focus:outline-none dark:bg-gray-800/90 dark:text-gray-100 dark:hover:bg-gray-800"
                onClick={() => setSelectedProof(null)}
                aria-label="Đóng"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-xl">
                <img
                  src={selectedProof.image_url}
                  alt={selectedProof.title}
                  className="w-full h-auto max-h-[80vh] object-contain bg-black"
                />
                <div className="px-4 py-3">
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{selectedProof.title}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-300 truncate">{selectedProof.user_name}</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
